import type { jsPDF as JsPdf } from 'jspdf';
import type { PaidReading } from '../types';
import { displayPaidReadingSchema } from '../types/reading-schemas';
import { toPaidReadingPresentation } from './paid-reading-presentation';

export const PAID_READING_PDF_DISCLAIMER = 'For entertainment and self-discovery purposes only. Not medical, psychological, legal, or financial advice.';
export const PAID_READING_PDF_IDLE_LABEL = 'Save My Reading as PDF';
export const PAID_READING_PDF_BUSY_LABEL = 'Preparing PDF…';
export const PAID_READING_PDF_ERROR = 'We could not prepare your PDF. Please try again.';
export const ALUNAR_ASTRO_WEBSITE = 'https://alunarastro.com';
export const ALUNAR_ASTRO_SOCIALS = [
  { platform: 'Instagram', label: '@alunarastro', url: 'https://www.instagram.com/alunarastro' },
  { platform: 'Instagram', label: '@alunarastrodaily', url: 'https://www.instagram.com/alunarastrodaily' },
  { platform: 'Facebook', label: 'Alunar Astro', url: 'https://www.facebook.com/profile.php?id=61592127877789' },
  { platform: 'Facebook', label: 'Alunar Astro Daily', url: 'https://www.facebook.com/profile.php?id=61588782912166' },
] as const;

export interface PaidReadingPdfField {
  label: string;
  text: string;
}

export interface PaidReadingPdfChapter {
  number: string;
  title: string;
  overview: string;
  details: PaidReadingPdfField[];
}

export interface PaidReadingPdfForecast {
  month: string;
  personalMonth: number;
  theme: string;
  details: PaidReadingPdfField[];
  reflection?: string;
  affirmation?: string;
}

export interface PaidReadingPdfDocument {
  brand: 'ALUNAR ASTRO';
  website: typeof ALUNAR_ASTRO_WEBSITE;
  documentTitle: 'Your Complete Celestial Reading';
  title: string;
  preparedFor: string;
  preparedDate?: string;
  numerologyBadges: string[];
  interpretiveQuote?: string;
  readingAtAGlance: PaidReadingPdfField[];
  chapters: PaidReadingPdfChapter[];
  forecastTitle: string;
  forecast: PaidReadingPdfForecast[];
  majorTurningPoint?: PaidReadingPdfChapter;
  sixMonthPattern?: PaidReadingPdfChapter;
  pathAhead: { title: string; message: string; closingQuote?: string };
  disclaimer: typeof PAID_READING_PDF_DISCLAIMER;
  socials: Array<{ platform: 'Instagram' | 'Facebook'; label: string; url: string }>;
}

export interface PaidReadingPdfActionState {
  label: string;
  disabled: boolean;
  error: string;
}

export type PaidReadingPdfSaver = (reading: PaidReading) => Promise<void>;
export interface PaidReadingPdfCoordinator { run: () => Promise<boolean> }

const IDLE_STATE: PaidReadingPdfActionState = { label: PAID_READING_PDF_IDLE_LABEL, disabled: false, error: '' };
const BUSY_STATE: PaidReadingPdfActionState = { label: PAID_READING_PDF_BUSY_LABEL, disabled: true, error: '' };
const ERROR_STATE: PaidReadingPdfActionState = { label: PAID_READING_PDF_IDLE_LABEL, disabled: false, error: PAID_READING_PDF_ERROR };

function optionalText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const clean = value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '').trim();
  return clean || undefined;
}

function requiredText(value: unknown): string { return optionalText(value) ?? ''; }

export function isPaidReadingPdfEligible(value: unknown): value is PaidReading {
  return displayPaidReadingSchema.safeParse(value).success;
}

export function formatPaidReadingDate(value: unknown): string | undefined {
  const text = optionalText(value);
  if (!text) return undefined;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return undefined;
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(date);
}

export function formatForecastMonth(value: string): string {
  if (!/^\d{4}-\d{2}$/.test(value)) return value;
  const date = new Date(`${value}-01T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
}

export function buildPaidReadingPdfDocument(reading: PaidReading): PaidReadingPdfDocument {
  const view = toPaidReadingPresentation(reading);
  const signature = view.celestialSignature;
  const forecastLabel = view.forecast.length === 6 ? 'Your Next Six Months' : `Your Next ${view.forecast.length} Months`;
  const numerology = signature?.numerology;
  const chapters: PaidReadingPdfChapter[] = [
    {
      number: 'I', title: 'Celestial Signature', overview: requiredText(signature?.overview),
      details: [
        ['Sun', signature?.sun], ['Moon', signature?.moon], ['Mercury', signature?.mercury], ['Venus', signature?.venus],
        ['Mars', signature?.mars], ['Jupiter', signature?.jupiter], ['Saturn', signature?.saturn], ['Numerology', numerology?.overview],
      ].flatMap(([label, value]) => optionalText(value) ? [{ label: String(label), text: requiredText(value) }] : []),
    },
  ];
  if (view.corePattern) chapters.push({ number: 'II', title: 'Your Core Pattern', overview: view.corePattern.overview, details: view.corePattern.details });
  chapters.push(
    { number: view.sourceVersion === 'paid-v3' ? 'III' : 'II', title: 'Love & Relationships', overview: view.loveAndRelationships.overview, details: view.loveAndRelationships.details },
    { number: view.sourceVersion === 'paid-v3' ? 'IV' : 'III', title: 'Career & Money', overview: view.careerAndMoney.overview, details: view.careerAndMoney.details },
    { number: view.sourceVersion === 'paid-v3' ? 'V' : 'IV', title: 'Purpose & Growth', overview: view.purposeAndGrowth.overview, details: view.purposeAndGrowth.details },
  );

  const forecast = view.forecast.map((month): PaidReadingPdfForecast => ({
    month: requiredText(month.month),
    personalMonth: month.personalMonth,
    theme: requiredText(month.theme),
    details: [
      ['Why This Month Feels Different', month.whyThisMonthFeelsDifferent], ['Opportunity', month.opportunity], ['Challenge', month.challenge], ['Love & Relationships', month.loveAndRelationships],
      ['Career & Money', month.careerAndMoney], ['Practical Step', month.practicalStep],
    ].flatMap(([label, value]) => optionalText(value) ? [{ label: String(label), text: requiredText(value) }] : []),
    reflection: optionalText(month.reflection),
    affirmation: optionalText(month.affirmation),
  }));

  return {
    brand: 'ALUNAR ASTRO',
    website: ALUNAR_ASTRO_WEBSITE,
    documentTitle: 'Your Complete Celestial Reading',
    title: requiredText(view.title),
    preparedFor: requiredText(view.preparedFor),
    preparedDate: formatPaidReadingDate(view.preparedAt),
    numerologyBadges: [numerology?.life_path, numerology?.birthday_number, numerology?.personal_year].flatMap((value) => optionalText(value) ?? []),
    interpretiveQuote: optionalText(view.interpretiveQuote),
    readingAtAGlance: view.readingAtAGlance?.map(({ label, text }) => ({ label, text })) ?? [],
    chapters,
    forecastTitle: forecastLabel,
    forecast,
    ...(view.majorTurningPoint ? { majorTurningPoint: { number: 'VII', title: `Your Major Turning Point · ${formatForecastMonth(view.majorTurningPoint.month)}`, overview: view.majorTurningPoint.title, details: view.majorTurningPoint.details } } : {}),
    ...(view.sixMonthPattern ? { sixMonthPattern: { number: 'VIII', title: view.forecast.length === 6 ? 'The Pattern Across Your Six Months' : `The Pattern Across Your ${view.forecast.length}-Month Horizon`, overview: view.sixMonthPattern.overview, details: view.sixMonthPattern.details } } : {}),
    pathAhead: { title: view.pathAhead.title, message: view.pathAhead.message, ...(view.pathAhead.closingQuote ? { closingQuote: view.pathAhead.closingQuote } : {}) },
    disclaimer: PAID_READING_PDF_DISCLAIMER,
    socials: ALUNAR_ASTRO_SOCIALS.map((social) => ({ ...social })),
  };
}

export function sanitizePdfFilenameName(value: unknown): string {
  const text = optionalText(value) ?? 'Reading';
  const safe = text.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
  return safe || 'Reading';
}

export function createPaidReadingPdfFilename(reading: Pick<PaidReading, 'prepared_for' | 'prepared_at'>, fallbackDate = new Date()): string {
  const prepared = typeof reading.prepared_at === 'string' && /^\d{4}-\d{2}-\d{2}/.test(reading.prepared_at) ? reading.prepared_at.slice(0, 10) : fallbackDate.toISOString().slice(0, 10);
  return `Alunar-Reading-${sanitizePdfFilenameName(reading.prepared_for)}-${prepared}.pdf`;
}

export async function runPaidReadingPdfAction(reading: PaidReading, save: PaidReadingPdfSaver, update: (state: PaidReadingPdfActionState) => void): Promise<boolean> {
  update({ ...BUSY_STATE });
  try {
    await save(reading);
    update({ ...IDLE_STATE });
    return true;
  } catch {
    update({ ...ERROR_STATE });
    return false;
  }
}

export function createPaidReadingPdfCoordinator(reading: PaidReading, save: PaidReadingPdfSaver, update: (state: PaidReadingPdfActionState) => void): PaidReadingPdfCoordinator {
  let active = false;
  return {
    run: async () => {
      if (active) return false;
      active = true;
      try { return await runPaidReadingPdfAction(reading, save, update); }
      finally { active = false; }
    },
  };
}

const PAGE = { width: 595.28, height: 841.89, margin: 50, top: 66, bottom: 58 } as const;
const COLOR = {
  background: [8, 7, 20] as const,
  panel: [18, 16, 42] as const,
  gold: [232, 192, 96] as const,
  goldMuted: [142, 112, 53] as const,
  parchment: [247, 239, 220] as const,
  muted: [205, 196, 177] as const,
  silver: [200, 232, 255] as const,
};

function pdfText(value: string): string {
  return value.replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/[–—]/g, '-').replace(/…/g, '...').replace(/\u00A0/g, ' ');
}

interface MeasuredMonthField { label: string; lines: string[]; accent: boolean }
interface MonthFieldFragment { label: string; lines: string[]; accent: boolean; continued: boolean }

class AlunarPdfRenderer {
  private y: number = PAGE.top;
  private pageNumber: number = 1;

  constructor(private readonly pdf: JsPdf) { this.paintPage(); }

  private paintPage(): void {
    this.pdf.setFillColor(...COLOR.background);
    this.pdf.rect(0, 0, PAGE.width, PAGE.height, 'F');
    this.pdf.setDrawColor(...COLOR.goldMuted);
    this.pdf.setLineWidth(.5);
    this.pdf.line(PAGE.margin, 38, PAGE.width - PAGE.margin, 38);
    this.pdf.setFont('times', 'bold');
    this.pdf.setFontSize(8.5);
    this.pdf.setTextColor(...COLOR.gold);
    this.pdf.text('ALUNAR ASTRO', PAGE.width / 2, 34, { align: 'center', charSpace: 1.8 });
    this.y = PAGE.top;
  }

  private addPage(): void {
    this.pdf.addPage();
    this.pageNumber += 1;
    this.paintPage();
  }

  private remaining(): number { return PAGE.height - PAGE.bottom - this.y; }

  private ensure(height: number): void { if (height > this.remaining()) this.addPage(); }

  private setFont(font: 'times' | 'helvetica', style: 'normal' | 'bold' | 'italic', size: number, color: readonly [number, number, number] = COLOR.parchment): void {
    this.pdf.setFont(font, style);
    this.pdf.setFontSize(size);
    this.pdf.setTextColor(...color);
  }

  private lines(text: string, width: number): string[] {
    return this.pdf.splitTextToSize(pdfText(text), width) as string[];
  }

  private ornament(y: number): void {
    const center = PAGE.width / 2;
    const innerGap = 12;
    const lineLength = 44;
    this.pdf.setDrawColor(...COLOR.gold);
    this.pdf.setFillColor(...COLOR.gold);
    this.pdf.line(center - innerGap - lineLength, y, center - innerGap, y);
    this.pdf.line(center + innerGap, y, center + innerGap + lineLength, y);
    this.pdf.rect(center - 2.5, y - 2.5, 5, 5, 'F');
  }

  private centeredLink(label: string, y: number, url: string): void {
    const text = pdfText(label);
    const x = PAGE.width / 2 - this.pdf.getTextWidth(text) / 2;
    this.pdf.textWithLink(text, x, y, { url });
  }

  cover(document: PaidReadingPdfDocument): void {
    this.y = 83;
    this.ornament(this.y);
    this.y += 30;
    this.setFont('times', 'bold', 27, COLOR.gold);
    this.pdf.text(document.brand, PAGE.width / 2, this.y, { align: 'center', charSpace: 3.2 });
    this.y += 26;
    this.setFont('helvetica', 'bold', 10, COLOR.silver);
    this.pdf.text(document.documentTitle.toUpperCase(), PAGE.width / 2, this.y, { align: 'center', charSpace: 1.2 });
    this.y += 32;
    this.setFont('times', 'bold', 24, COLOR.parchment);
    const titleLines = this.lines(document.title, PAGE.width - PAGE.margin * 2 - 30);
    for (const line of titleLines) { this.pdf.text(line, PAGE.width / 2, this.y, { align: 'center' }); this.y += 29; }
    this.y += 9;
    this.setFont('times', 'italic', 13, COLOR.muted);
    this.pdf.text(`Prepared for ${pdfText(document.preparedFor)}`, PAGE.width / 2, this.y, { align: 'center' });
    if (document.preparedDate) {
      this.y += 18;
      this.setFont('helvetica', 'normal', 8.5, COLOR.muted);
      this.pdf.text(document.preparedDate.toUpperCase(), PAGE.width / 2, this.y, { align: 'center', charSpace: .8 });
    }
    this.y += 28;
    this.badges(document.numerologyBadges);
    this.y += 15;
    this.setFont('helvetica', 'normal', 8.3, COLOR.gold);
    this.centeredLink('alunarastro.com', this.y, document.website);
    this.y += 12;
  }

  private badges(badges: string[]): void {
    const values = badges.filter(Boolean);
    if (!values.length) return;
    const gap = 8;
    const width = (PAGE.width - PAGE.margin * 2 - gap * (values.length - 1)) / values.length;
    const height = 30;
    this.ensure(height);
    values.forEach((badge, index) => {
      const x = PAGE.margin + index * (width + gap);
      this.pdf.setFillColor(...COLOR.panel);
      this.pdf.setDrawColor(...COLOR.goldMuted);
      this.pdf.roundedRect(x, this.y, width, height, 3, 3, 'FD');
      this.setFont('helvetica', 'bold', 7.5, COLOR.gold);
      const label = this.lines(badge.toUpperCase(), width - 8)[0] ?? '';
      this.pdf.text(label, x + width / 2, this.y + 18.5, { align: 'center', charSpace: .25 });
    });
    this.y += height;
  }

  section(number: string, title: string): void {
    this.ensure(64);
    this.y += 12;
    this.setFont('helvetica', 'bold', 8.5, COLOR.gold);
    this.pdf.text(number, PAGE.margin, this.y, { charSpace: 1.4 });
    this.y += 22;
    this.setFont('times', 'bold', 19, COLOR.gold);
    this.pdf.text(pdfText(title), PAGE.margin, this.y);
    this.y += 10;
    this.pdf.setDrawColor(...COLOR.goldMuted);
    this.pdf.setLineWidth(.6);
    this.pdf.line(PAGE.margin, this.y, PAGE.width - PAGE.margin, this.y);
    this.y += 18;
  }

  paragraph(text: string, options: { italic?: boolean; color?: readonly [number, number, number] } = {}): void {
    if (!text) return;
    const lineHeight = 15;
    this.setFont('helvetica', options.italic ? 'italic' : 'normal', 10.5, options.color ?? COLOR.muted);
    const lines = this.lines(text, PAGE.width - PAGE.margin * 2);
    for (const line of lines) {
      this.ensure(lineHeight);
      this.pdf.text(line, PAGE.margin, this.y);
      this.y += lineHeight;
    }
    this.y += 9;
  }

  labeledBlock(label: string, text: string, accent = false): void {
    if (!text) return;
    this.setFont('helvetica', 'normal', 9.5, COLOR.muted);
    const lines = this.lines(text, PAGE.width - PAGE.margin * 2 - 22);
    const lineHeight = 13.2;
    let first = true;
    let index = 0;
    while (index < lines.length) {
      if (this.remaining() < 54) this.addPage();
      const availableLines = Math.max(1, Math.floor((this.remaining() - 34) / lineHeight));
      const part = lines.slice(index, index + availableLines);
      const height = 26 + part.length * lineHeight;
      this.pdf.setFillColor(...COLOR.panel);
      const borderColor: readonly [number, number, number] = accent ? COLOR.gold : COLOR.goldMuted;
      this.pdf.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
      this.pdf.setLineWidth(.45);
      this.pdf.roundedRect(PAGE.margin, this.y, PAGE.width - PAGE.margin * 2, height, 4, 4, 'FD');
      this.setFont('helvetica', 'bold', 8.3, accent ? COLOR.gold : COLOR.silver);
      this.pdf.text(`${pdfText(label).toUpperCase()}${first ? '' : ' (CONTINUED)'}`, PAGE.margin + 11, this.y + 15, { charSpace: .55 });
      this.setFont('helvetica', 'normal', 9.5, COLOR.muted);
      part.forEach((line, lineIndex) => this.pdf.text(line, PAGE.margin + 11, this.y + 30 + lineIndex * lineHeight));
      this.y += height + 8;
      index += part.length;
      first = false;
      if (index < lines.length) this.addPage();
    }
  }

  private measuredMonthFields(month: PaidReadingPdfForecast): MeasuredMonthField[] {
    const width = PAGE.width - PAGE.margin * 2 - 32;
    const fields: Array<{ label: string; text: string | undefined; accent: boolean }> = [
      { label: 'Theme', text: month.theme, accent: true },
      ...month.details.map((field) => ({ ...field, accent: false })),
      { label: 'Reflection', text: month.reflection, accent: true },
      { label: 'Affirmation', text: month.affirmation, accent: true },
    ];
    this.setFont('helvetica', 'normal', 9.5, COLOR.muted);
    return fields.flatMap((field) => field.text ? [{ label: field.label, lines: this.lines(field.text, width), accent: field.accent }] : []);
  }

  private monthFieldHeight(lineCount: number): number { return 30 + lineCount * 13.2; }

  private drawMonthFragment(month: PaidReadingPdfForecast, index: number, total: number, fields: MonthFieldFragment[], continued: boolean): void {
    const headerHeight = 52;
    const cardHeight = headerHeight + fields.reduce((height, field) => height + this.monthFieldHeight(field.lines.length), 0) + 12;
    const top = this.y;
    const left = PAGE.margin;
    const width = PAGE.width - PAGE.margin * 2;
    this.pdf.setFillColor(...COLOR.panel);
    this.pdf.setDrawColor(...COLOR.gold);
    this.pdf.setLineWidth(.75);
    this.pdf.roundedRect(left, top, width, cardHeight, 6, 6, 'FD');
    this.setFont('helvetica', 'bold', 8.2, COLOR.gold);
    const monthLabel = `MONTH ${index + 1} OF ${total} · ${formatForecastMonth(month.month).toUpperCase()}${continued ? ' · CONTINUED' : ''}`;
    this.pdf.text(monthLabel, left + 15, top + 21, { charSpace: .45 });
    this.setFont('helvetica', 'bold', 8.5, COLOR.silver);
    this.pdf.text(`PERSONAL MONTH ${month.personalMonth}`, left + width - 15, top + 21, { align: 'right', charSpace: .25 });
    this.pdf.setDrawColor(...COLOR.goldMuted);
    this.pdf.setLineWidth(.4);
    this.pdf.line(left + 15, top + 37, left + width - 15, top + 37);
    let cursor = top + headerHeight;
    fields.forEach((field, fieldIndex) => {
      this.setFont('helvetica', 'bold', 8.2, field.accent ? COLOR.gold : COLOR.silver);
      this.pdf.text(`${pdfText(field.label).toUpperCase()}${field.continued ? ' (CONTINUED)' : ''}`, left + 15, cursor + 9, { charSpace: .45 });
      this.setFont('helvetica', field.label === 'Affirmation' ? 'italic' : 'normal', 9.5, field.label === 'Affirmation' ? COLOR.parchment : COLOR.muted);
      field.lines.forEach((line, lineIndex) => this.pdf.text(line, left + 15, cursor + 25 + lineIndex * 13.2));
      cursor += this.monthFieldHeight(field.lines.length);
      if (fieldIndex < fields.length - 1) {
        this.pdf.setDrawColor(...COLOR.goldMuted);
        this.pdf.setLineWidth(.25);
        this.pdf.line(left + 15, cursor - 5, left + width - 15, cursor - 5);
      }
    });
    this.y = top + cardHeight + 16;
  }

  monthCard(month: PaidReadingPdfForecast, index: number, total: number): void {
    const fields = this.measuredMonthFields(month);
    const fullHeight = 64 + fields.reduce((height, field) => height + this.monthFieldHeight(field.lines.length), 0);
    const usableHeight = PAGE.height - PAGE.top - PAGE.bottom;
    if (fullHeight <= usableHeight) {
      if (fullHeight > this.remaining()) this.addPage();
      this.drawMonthFragment(month, index, total, fields.map((field) => ({ ...field, continued: false })), false);
      return;
    }

    if (this.y > PAGE.top + 1) this.addPage();
    let fieldIndex = 0;
    let lineIndex = 0;
    let fragmentIndex = 0;
    while (fieldIndex < fields.length) {
      const maxCardHeight = this.remaining();
      let used = 64;
      const fragment: MonthFieldFragment[] = [];
      while (fieldIndex < fields.length) {
        const field = fields[fieldIndex]!;
        const remainingLines = field.lines.slice(lineIndex);
        const completeHeight = this.monthFieldHeight(remainingLines.length);
        if (used + completeHeight <= maxCardHeight) {
          fragment.push({ label: field.label, lines: remainingLines, accent: field.accent, continued: lineIndex > 0 });
          used += completeHeight;
          fieldIndex += 1;
          lineIndex = 0;
          continue;
        }
        if (fragment.length) break;
        const lineCapacity = Math.max(1, Math.floor((maxCardHeight - used - 30) / 13.2));
        const lines = remainingLines.slice(0, lineCapacity);
        fragment.push({ label: field.label, lines, accent: field.accent, continued: lineIndex > 0 });
        lineIndex += lines.length;
        if (lineIndex >= field.lines.length) { fieldIndex += 1; lineIndex = 0; }
        break;
      }
      this.drawMonthFragment(month, index, total, fragment, fragmentIndex > 0);
      fragmentIndex += 1;
      if (fieldIndex < fields.length) this.addPage();
    }
  }

  private socialIcon(platform: 'Instagram' | 'Facebook', x: number, y: number): void {
    this.pdf.setDrawColor(...COLOR.gold);
    this.pdf.setLineWidth(.8);
    if (platform === 'Instagram') {
      this.pdf.roundedRect(x, y - 8, 13, 13, 3, 3, 'S');
      this.pdf.circle(x + 6.5, y - 1.5, 3, 'S');
      this.pdf.setFillColor(...COLOR.gold);
      this.pdf.circle(x + 10, y - 5, .8, 'F');
      return;
    }
    this.pdf.circle(x + 6.5, y - 1.5, 6.5, 'S');
    this.setFont('helvetica', 'bold', 11, COLOR.gold);
    this.pdf.text('f', x + 6.5, y + 2, { align: 'center' });
  }

  private finaleHeader(document: PaidReadingPdfDocument, continued: boolean): void {
    this.y = 88;
    this.ornament(this.y);
    this.y += 30;
    this.setFont('helvetica', 'bold', 8.5, COLOR.gold);
    this.pdf.text(`${document.majorTurningPoint ? 'IX' : 'VI'}${continued ? ' · CONTINUED' : ''}`, PAGE.width / 2, this.y, { align: 'center', charSpace: 1.4 });
    this.y += 29;
    this.setFont('times', 'bold', 25, COLOR.gold);
    this.pdf.text('A Message for the Path Ahead', PAGE.width / 2, this.y, { align: 'center' });
    this.y += 28;
    this.setFont('helvetica', 'bold', 9, COLOR.silver);
    this.pdf.text(pdfText(document.pathAhead.title).toUpperCase(), PAGE.width / 2, this.y, { align: 'center', charSpace: .7 });
    this.y += 23;
  }

  private closingPanel(lines: string[]): number {
    const left = PAGE.margin + 18;
    const width = PAGE.width - (PAGE.margin + 18) * 2;
    const height = Math.max(112, 48 + lines.length * 18.5);
    this.pdf.setFillColor(22, 18, 48);
    this.pdf.setDrawColor(...COLOR.gold);
    this.pdf.setLineWidth(.8);
    this.pdf.roundedRect(left, this.y, width, height, 8, 8, 'FD');
    this.pdf.setDrawColor(...COLOR.goldMuted);
    this.pdf.setLineWidth(.3);
    this.pdf.roundedRect(left + 5, this.y + 5, width - 10, height - 10, 6, 6, 'S');
    this.setFont('times', 'italic', 13.5, COLOR.parchment);
    const start = this.y + (height - (lines.length - 1) * 18.5) / 2 + 2;
    lines.forEach((line, index) => this.pdf.text(line, PAGE.width / 2, start + index * 18.5, { align: 'center' }));
    this.y += height;
    return height;
  }

  private finalLinks(document: PaidReadingPdfDocument): void {
    this.y = Math.max(this.y + 31, 465);
    this.ornament(this.y);
    this.y += 28;
    this.setFont('times', 'bold', 19, COLOR.gold);
    this.pdf.text(document.brand, PAGE.width / 2, this.y, { align: 'center', charSpace: 2.1 });
    this.y += 19;
    this.setFont('helvetica', 'normal', 8.8, COLOR.silver);
    this.centeredLink(document.website, this.y, document.website);
    this.y += 31;
    this.setFont('helvetica', 'bold', 9, COLOR.gold);
    this.pdf.text('FOLLOW ALUNAR ASTRO', PAGE.width / 2, this.y, { align: 'center', charSpace: 1.2 });
    this.y += 27;
    const columns = [PAGE.margin + 65, PAGE.width / 2 + 40];
    (['Instagram', 'Facebook'] as const).forEach((platform, columnIndex) => {
      const x = columns[columnIndex]!;
      this.socialIcon(platform, x, this.y);
      this.setFont('helvetica', 'bold', 8.2, COLOR.gold);
      this.pdf.text(platform.toUpperCase(), x + 21, this.y, { charSpace: .6 });
      document.socials.filter((social) => social.platform === platform).forEach((social, socialIndex) => {
        this.setFont('helvetica', 'normal', 8.2, COLOR.silver);
        this.pdf.textWithLink(pdfText(social.label), x + 21, this.y + 18 + socialIndex * 17, { url: social.url });
      });
    });
    this.y += 78;
    this.setFont('helvetica', 'normal', 7.8, COLOR.muted);
    const disclaimer = this.lines(document.disclaimer, PAGE.width - PAGE.margin * 2 - 70);
    disclaimer.forEach((line) => { this.pdf.text(line, PAGE.width / 2, this.y, { align: 'center' }); this.y += 11; });
  }

  finale(document: PaidReadingPdfDocument): void {
    this.setFont('times', 'italic', 13.5, COLOR.parchment);
    const closingText = [document.pathAhead.message, document.pathAhead.closingQuote ? `“${document.pathAhead.closingQuote}”` : ''].filter(Boolean).join('\n\n');
    const lines = this.lines(closingText, PAGE.width - PAGE.margin * 2 - 90);
    const finalCapacity = 13;
    const regularCapacity = 27;
    const chunks: string[][] = [];
    if (lines.length <= finalCapacity) chunks.push(lines);
    else {
      const beforeFinal = lines.length - finalCapacity;
      const preliminaryPages = Math.ceil(beforeFinal / regularCapacity);
      const preliminarySize = Math.ceil(beforeFinal / preliminaryPages);
      let cursor = 0;
      for (let page = 0; page < preliminaryPages; page += 1) { chunks.push(lines.slice(cursor, cursor + preliminarySize)); cursor += preliminarySize; }
      chunks.push(lines.slice(cursor));
    }
    chunks.forEach((chunk, index) => {
      this.addPage();
      this.finaleHeader(document, index > 0);
      this.closingPanel(chunk);
      if (index === chunks.length - 1) this.finalLinks(document);
    });
  }

  finish(): void {
    const total = this.pdf.getNumberOfPages();
    for (let page = 1; page <= total; page += 1) {
      this.pdf.setPage(page);
      this.pdf.setDrawColor(...COLOR.goldMuted);
      this.pdf.setLineWidth(.35);
      this.pdf.line(PAGE.margin, PAGE.height - 35, PAGE.width - PAGE.margin, PAGE.height - 35);
      this.setFont('helvetica', 'normal', 7.5, COLOR.muted);
      const left = 'ALUNAR ASTRO · ';
      const website = 'alunarastro.com';
      const right = ` · ${page} / ${total}`;
      const fullWidth = this.pdf.getTextWidth(left) + this.pdf.getTextWidth(website) + this.pdf.getTextWidth(right);
      let x = PAGE.width / 2 - fullWidth / 2;
      this.pdf.text(left, x, PAGE.height - 21);
      x += this.pdf.getTextWidth(left);
      this.pdf.textWithLink(website, x, PAGE.height - 21, { url: ALUNAR_ASTRO_WEBSITE });
      x += this.pdf.getTextWidth(website);
      this.pdf.text(right, x, PAGE.height - 21);
    }
    this.pdf.setPage(this.pageNumber);
  }
}

export async function generatePaidReadingPdfBlob(reading: PaidReading): Promise<Blob> {
  if (!isPaidReadingPdfEligible(reading)) throw new Error('paid_reading_unavailable');
  const document = buildPaidReadingPdfDocument(reading);
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: false, putOnlyUsedFonts: false });
  pdf.setProperties({
    title: 'Alunar Astro — Personal Astrology Reading',
    subject: 'Personal Astrology Reading',
    author: 'Alunar Astro',
    creator: 'Alunar Astro',
  });
  const renderer = new AlunarPdfRenderer(pdf);
  renderer.cover(document);
  if (document.interpretiveQuote) renderer.paragraph(document.interpretiveQuote, { italic: true, color: COLOR.parchment });
  if (document.readingAtAGlance.length) {
    renderer.section('', 'Reading at a Glance');
    document.readingAtAGlance.forEach((field) => renderer.labeledBlock(field.label, field.text, field.label === 'Major Turning Point'));
  }
  for (const chapter of document.chapters) {
    renderer.section(chapter.number, chapter.title);
    renderer.paragraph(chapter.overview, { italic: true, color: COLOR.parchment });
    chapter.details.forEach((field) => renderer.labeledBlock(field.label, field.text, field.label === 'Numerology'));
  }
  renderer.section(document.majorTurningPoint ? 'VI' : 'V', document.forecastTitle);
  renderer.paragraph('A month-by-month guide to the themes, openings, and choices ahead.');
  document.forecast.forEach((month, index) => renderer.monthCard(month, index, document.forecast.length));
  for (const chapter of [document.majorTurningPoint, document.sixMonthPattern]) {
    if (!chapter) continue;
    renderer.section(chapter.number, chapter.title);
    renderer.paragraph(chapter.overview, { italic: true, color: COLOR.parchment });
    chapter.details.forEach((field) => renderer.labeledBlock(field.label, field.text, true));
  }
  renderer.finale(document);
  renderer.finish();
  return new Blob([pdf.output('arraybuffer')], { type: 'application/pdf' });
}

export function isMobileSafari(userAgent: string, platform: string, maxTouchPoints: number): boolean {
  const ios = /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1);
  return ios && /Safari/.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(userAgent);
}

export async function savePaidReadingAsPdf(reading: PaidReading): Promise<void> {
  const mobileSafari = isMobileSafari(navigator.userAgent, navigator.platform, navigator.maxTouchPoints);
  const supportsDownload = 'download' in document.createElement('a');
  const preview = mobileSafari && !supportsDownload ? window.open('', '_blank') : null;
  if (preview) preview.opener = null;
  try {
    const blob = await generatePaidReadingPdfBlob(reading);
    const url = URL.createObjectURL(blob);
    if (preview) {
      preview.location.href = url;
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = createPaidReadingPdfFilename(reading);
      link.rel = 'noopener noreferrer';
      if (!supportsDownload) link.target = '_blank';
      link.hidden = true;
      document.body.append(link);
      link.click();
      link.remove();
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    preview?.close();
    throw error;
  }
}
